import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface AppError {
  id: string;
  message: string;
  code?: string;
  timestamp: Date;
  isRead: boolean;
}

interface ErrorContextType {
  errors: AppError[];
  unreadCount: number;
  addError: (message: string, code?: string) => void;
  removeError: (id: string) => void;
  markAsRead: (id: string) => void;
  clearAll: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
}

export function ErrorProvider({ children }: ErrorProviderProps) {
  const [errors, setErrors] = useState<AppError[]>([]);

  const addError = useCallback((message: string, code?: string) => {
    const error: AppError = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      message,
      code,
      timestamp: new Date(),
      isRead: false,
    };
    setErrors((prev) => [...prev, error]);
  }, []);

  const removeError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const markAsRead = useCallback((id: string) => {
    setErrors((prev) =>
      prev.map((e) => (e.id === id ? { ...e, isRead: true } : e))
    );
  }, []);

  const clearAll = useCallback(() => {
    setErrors([]);
  }, []);

  const unreadCount = errors.filter((e) => !e.isRead).length;

  return (
    <ErrorContext.Provider
      value={{ errors, unreadCount, addError, removeError, markAsRead, clearAll }}
    >
      {children}
    </ErrorContext.Provider>
  );
}

export function useErrors(): ErrorContextType {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrors must be used within ErrorProvider');
  }
  return context;
}
